import { Input, LuIcon, Separator } from "@overmap-ai/blocks"
import { includesLowercase } from "@overmap-ai/common"
import { useTextFilter } from "@overmap-ai/hooks"
import * as RadixToggleGroup from "@radix-ui/react-toggle-group"
import { useVirtualizer } from "@tanstack/react-virtual"
import { cx } from "class-variance-authority"
import { dynamicIconImports } from "lucide-react/dynamic"
import {
	type ChangeEventHandler,
	type ComponentPropsWithRef,
	type CSSProperties,
	memo,
	useCallback,
	useMemo,
	useRef,
} from "react"
import { twMerge } from "tailwind-merge"

import { scrollbarsCva } from "../../cva"
import { modelIconMapping } from "../../mappings/icons"
import { Colors } from "../../utils/colors"
import { LuDynamicIcon } from "../Icons/LuDynamicIcon"
import type { IconType } from "../Icons/typings"
import supportedIcons from "./icons.json"

const supportedIconsSet: Set<string> = new Set(supportedIcons)

// @ts-expect-error REASON: we only pass in options of IconType
const icons: IconType[] = Object.keys(dynamicIconImports).filter((key) => supportedIconsSet.has(key))

const NUMBER_OF_COLUMNS = 10

const iconFilterCallback = (icon: IconType, filterValue: string) => {
	return includesLowercase(icon, filterValue)
}

export interface IconColorPickerProps extends Omit<ComponentPropsWithRef<"div">, "color"> {
	selectedIcon: string | null
	onIconChange: (icon: IconType | null) => void
	color: string | null
	onColorChange: (color: string | null) => void
}

export const IconColorPicker = memo<IconColorPickerProps>((props) => {
	const { className, ref, selectedIcon, onIconChange, color, onColorChange } = props

	const parentRef = useRef<HTMLDivElement>(null)

	const [filteredIcons, fitlerValue, setFilterValue] = useTextFilter(icons, iconFilterCallback)

	const handleFilterInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		(e) => {
			setFilterValue(e.target.value)
		},
		[setFilterValue],
	)

	const computedIcons = useMemo(() => {
		const partitionedIcons: IconType[][] = []

		for (let i = 0; i < filteredIcons.length; i += NUMBER_OF_COLUMNS) {
			partitionedIcons.push(filteredIcons.slice(i, i + NUMBER_OF_COLUMNS))
		}
		return partitionedIcons
	}, [filteredIcons])

	const rowVirtualizer = useVirtualizer({
		count: computedIcons.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 28,
		overscan: 3,
	})

	const iconSelectStyles = useMemo(
		() =>
			({
				"--hover-color": `${color}30`,
				"--active-color": `${color}40`,
			}) as CSSProperties,
		[color],
	)

	return (
		<div className={twMerge(cx("flex size-full flex-col gap-2", className))} ref={ref}>
			<div className="flex h-max w-full">
				<RadixToggleGroup.Root
					className="flex w-full flex-wrap gap-1 py-1"
					type="single"
					value={color ?? undefined}
					onValueChange={onColorChange}
				>
					{Object.values(Colors).map((color) => (
						<RadixToggleGroup.Item
							key={color}
							className="outline-(--primary-indicator) size-6 rounded-full data-[state=on]:outline-2"
							style={{ backgroundColor: color }}
							value={color}
						/>
					))}
				</RadixToggleGroup.Root>
			</div>

			<Separator size="full" />

			<Input.Root variant="outline" size="sm">
				<Input.Slot>
					<LuIcon icon={modelIconMapping.search} />
				</Input.Slot>
				<Input.Field placeholder="Search by name" value={fitlerValue} onChange={handleFilterInputChange} />
			</Input.Root>

			<RadixToggleGroup.Root
				ref={parentRef}
				className={cx("h-[200px] w-full overflow-auto", scrollbarsCva())}
				style={iconSelectStyles}
				type="single"
				value={selectedIcon ?? undefined}
				// @ts-expect-error REASON: we only pass in options of IconType
				onValueChange={onIconChange}
			>
				<div
					className="relative w-full"
					style={{
						height: `${rowVirtualizer.getTotalSize()}px`,
					}}
				>
					{rowVirtualizer.getVirtualItems().map((virtualRow) => (
						<div
							className="absolute left-0 top-0 flex w-full"
							key={virtualRow.key}
							ref={rowVirtualizer.measureElement}
							style={{
								height: `${virtualRow.size}px`,
								transform: `translateY(${virtualRow.start}px)`,
							}}
							data-index={virtualRow.index}
						>
							{computedIcons[virtualRow.index]?.map((iconName) => {
								return (
									<RadixToggleGroup.Item
										className="hover:bg-(--hover-color) data-[state=on]:bg-(--active-color) flex size-7 items-center justify-center rounded-lg p-3.5 text-sm"
										value={iconName}
										key={iconName}
									>
										<LuDynamicIcon color={color ?? undefined} icon={iconName} />
									</RadixToggleGroup.Item>
								)
							})}
						</div>
					))}
				</div>
			</RadixToggleGroup.Root>
		</div>
	)
})
IconColorPicker.displayName = "IconColorPicker"
